import { ArrowDropUp, ArrowDropDown, Clear, Add } from '@mui/icons-material';
import { Grid, Typography, Button, IconButton } from '@mui/material';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { ProfileItemRequest } from 'src/modules/member/models/profileItem';
import { useFieldArray, useFormContext } from 'react-hook-form';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { useState } from 'react';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';

type DeleteProfileData = {
  openDeleteDialog: boolean;
  index: number | undefined;
  profileMemberCount: number;
};

type FormData = {
  profileItems: ProfileItemRequest[];
  deleteProfileItemsIds: number[];
};

type ProfileItemSectionProps = {
  memberProfileId: number;
  syncPositions: () => void;
  loading: boolean;
  isEditable: boolean;
};

export const ProfileItemSection = (props: ProfileItemSectionProps) => {
  const { memberProfileId, syncPositions, loading, isEditable } = props;
  const { t } = useTranslation();

  const [deleteProfileData, setDeleteProfileData] = useState<DeleteProfileData>({ openDeleteDialog: false, index: undefined, profileMemberCount: 0 });
  const { control, setValue, getValues } = useFormContext<FormData>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'profileItems',
  });

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    move(index, index - 1);
    syncPositions();
  };

  const handleMoveDown = (index: number) => {
    if (index === fields.length - 1) return;
    move(index, index + 1);
    syncPositions();
  };

  const handleDelete = (index: number) => {
    const itemToDelete = getValues(`profileItems.${index}`);

    if (itemToDelete?.memberProfileItem_id) {
      const currentDeleted = getValues('deleteProfileItemsIds') || [];
      setValue('deleteProfileItemsIds', [...currentDeleted, itemToDelete.memberProfileItem_id]);
    }

    remove(index);
    setDeleteProfileData({
      openDeleteDialog: false,
      index: undefined,
      profileMemberCount: 0,
    });
  };

  const createEmptyProfileItem = (): ProfileItemRequest => ({
    memberProfileItem_id: null,
    memberProfile_id: memberProfileId,
    name: '',
    pos: fields.length + 1,
    groupName: '',
    text: '',
    profileMemberCount: 0,
  });

  return (
    <>
      <Grid item xs={12}>
        <Typography variant='h6' sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}>
          {t('memberProfile:profileDetail.profiles')}
        </Typography>

        {fields.map((profile, index) => (
          <Grid container alignItems='center' justifyContent='space-between' sx={{ py: 1 }} key={profile.id}>
            <Grid item xs={8}>
              <ControlledTextField
                disabled={loading || !isEditable}
                label={t('memberProfile:createProfile.profileName')}
                name={`profileItems.${index}.name`}
              />
            </Grid>
            <Box sx={{ mr: -1 }}>
              <IconButton
                sx={{ mr: -1 }}
                onClick={() => {
                  handleMoveUp(index);
                }}
                disabled={index === 0 || loading || !isEditable}
              >
                <ArrowDropUp />
              </IconButton>

              <IconButton
                onClick={() => {
                  handleMoveDown(index);
                }}
                disabled={index === fields.length - 1 || loading || !isEditable}
              >
                <ArrowDropDown />
              </IconButton>

              <IconButton
                onClick={() => {
                  setDeleteProfileData({
                    openDeleteDialog: true,
                    index,
                    profileMemberCount: profile.profileMemberCount || 0,
                  });
                }}
                disabled={loading || !isEditable}
              >
                <Clear color={isEditable ? 'error' : 'disabled'} />
              </IconButton>
            </Box>
          </Grid>
        ))}
        <Grid item xs={12} mt={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size='small'
            variant='outlined'
            endIcon={<Add />}
            disabled={loading || !isEditable}
            onClick={() => append(createEmptyProfileItem())}
          >
            {t('common:button.add')}
          </Button>
        </Grid>
      </Grid>
      {deleteProfileData.openDeleteDialog && (
        <DeleteConfirmationDialog
          title={t('common:confirmDelete')}
          modaltext={
            deleteProfileData.profileMemberCount === 1
              ? t('memberProfile:profileDetail.profileWarning', { count: deleteProfileData.profileMemberCount })
              : t('memberProfile:profileDetail.profileWarningPlural', { count: deleteProfileData.profileMemberCount })
          }
          value=''
          open={deleteProfileData.openDeleteDialog}
          onClose={(val) => {
            if (val) {
              if (deleteProfileData.index !== undefined) handleDelete(deleteProfileData.index);
            } else {
              setDeleteProfileData({
                openDeleteDialog: false,
                index: undefined,
                profileMemberCount: 0,
              });
            }
          }}
        />
      )}
    </>
  );
};
